import { components, type GroupBase } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { colors } from "../../colors";
import { Additional, OptionType, PaginateSelectProps } from "./types";

export function MadfarmPaginateSelect<T = any, Multi extends boolean = false>(
  props: PaginateSelectProps<T, Multi>
) {
  const CustomInput = (props: any) => {
    return <components.Input {...props} autoComplete="off" />;
  };

  const isMulti = props.isMulti ?? false;
  type IsMulti = typeof isMulti;

  return (
    <div className="w-full flex flex-col gap-[4px] z-40">
      {props.label && <span className="input-label">{props.label} {props.required && <span className="text-error-default"> *</span>}</span>}
      <AsyncPaginate<
        OptionType<T>,
        GroupBase<OptionType<T>>,
        Additional,
        IsMulti
      >
        components={{ Input: CustomInput }}
        id="madfarm-async-select"
        name={"non_autofill_field_1"}
        loadOptions={props.loadOptions}
        cacheUniqs={props.cacheUniqs}
        defaultValue={props.defaultValue}
        value={props.value ?? props.defaultValue}
        defaultOptions
        loadOptionsOnMenuOpen={false}
        onChange={props.onChange as any}
        menuPortalTarget={props.menuPortalTargetIsNotBody ? undefined : document.body}
        menuPlacement="auto"
        menuIsOpen={props.menuIsOpen}
        isClearable={props.isClearable}
        isMulti={props.isMulti || false}
        isDisabled={props.disabled}
        noOptionsMessage={() => <span className="text-xs">{props.noOptionsMessage}</span>}
        placeholder={props.placeholder}
        onMenuScrollToBottom={props.onMenuScrollToBottom}
        closeMenuOnSelect={true}
        styles={{
          placeholder: (baseStyles) => ({
            ...baseStyles,
            fontSize: '14px',
            lineHeight: '16px',
            color: colors.neutral[400],
            fontFamily: 'Manrope',
            fontWeight: 500
          }),
          option: (baseStyles, props) => ({
            ...baseStyles,
            fontSize: '14px',
            lineHeight: '20px',
            color: props.isSelected ? colors.white : colors.neutral[600],
            fontFamily: 'Manrope',
            fontWeight: 500,
            backgroundColor: props.isSelected ? colors.neutral[200] : colors.white,
            '&:hover': {
              backgroundColor: colors.brandPrimary[200]
            }
          }),
          input: (baseStyles, props) => ({
            ...baseStyles,
            fontSize: '14px',
            lineHeight: '20px',
            color: colors.neutral[600],
            fontFamily: 'Manrope',
            fontWeight: 500
          }),
          singleValue: (baseStyles, props) => ({
            ...baseStyles,
            fontSize: '14px',
            lineHeight: '20px',
            color: colors.neutral[600],
            fontFamily: 'Manrope',
            fontWeight: 500
          }),
          multiValue: (baseStyles, props) => ({
            ...baseStyles,
            fontSize: '14px',
            lineHeight: '20px',
            color: colors.brandPrimary[900],
            fontFamily: 'Manrope',
            fontWeight: 500,
            padding: '0px 8px',
            minWidth: 'auto',
            borderRadius: 8,
            backgroundColor: colors.brandPrimary[200],
            border: '1px solid ' + colors.brandPrimary[400],
            overflow: 'hidden',
            textOverflow: 'ellipsis',  // Applica l'ellisse quando necessario
            whiteSpace: 'nowrap',  // Impedisce la rottura di riga
            maxWidth: '100%',  // Limita la larghezza massima
          }),
          multiValueLabel: (baseStyles, props) => ({
            ...baseStyles,
            width: '100%',
            display: "block",
            overflow: 'hidden',  // Nasconde l'overflow
            textOverflow: 'ellipsis',  // Applica l'ellisse per i testi lunghi
            whiteSpace: 'nowrap',  // Impedisce la rottura di riga
            maxWidth: 'calc(100% - 20px)',  // Lascia spazio per l'icona di rimozione
          }),
          multiValueRemove: (baseStyles, props) => ({
            ...baseStyles,
            color: colors.neutral[500],
            '&:hover': {
              color: colors.brandPrimary[600],
              backgroundColor: colors.brandPrimary[200],
            }
          }),
          indicatorsContainer: (baseStyles) => ({
            ...baseStyles,
            alignItems: 'center',
            height: props.keepOverflow ? '40px' : undefined
          }),
          container: (baseStyles) => ({
            ...baseStyles,
            width: '100%',
            maxWidth: '100%',
            minHeight: "40px",
            height: props.keepOverflow ? '40px' : undefined,
          }),
          control: (baseStyles, state) => ({
            ...baseStyles,
            minHeight: "40px",
            height: props.keepOverflow ? '40px' : undefined,
            width: '100%',
            maxWidth: '100%',
            borderRadius: '8px',
            backgroundColor: props.disabled ? 'rgba(242, 242, 250, 1)' : 'white',
            boxShadow: 'none',
            border: (props.error ? '1px solid ' + colors.red[300] : state.isFocused ? '1px solid ' + colors.neutral[400] : '1px solid ' + colors.neutral[400]),
            fontFamily: 'Manrope',
            padding: '0px 12px',
            '&:hover': {
              border: '1px neutral ' + colors.neutral[400]
            }
          }),
          valueContainer: (baseStyles, state) => ({
            ...baseStyles,
            padding: props.keepOverflow ? 0 : "4px 0px",
            height: props.keepOverflow ? '40px' : undefined,
            overflow: "auto",
          }),
          indicatorSeparator: (baseStyles, state) => ({
            display: 'none'
          }),
          dropdownIndicator: (baseStyles, state) => ({
            color: state.isFocused ? colors.neutral[500] : colors.neutral[500]
          }),
          menuPortal: base => ({ ...base, zIndex: 50 })
        }}
      />
      {props.error && <span className="text-text-sm text-red-500">{props.errorLabel}</span>}
    </div>
  );
};
